//
// Copyright 2024 DXOS.org
//

import * as EffectContext from 'effect/Context';

import { type CleanupFn, Event } from '@dxos/async';
import { type Context, ContextDisposedError, LifecycleState, Resource } from '@dxos/context';
import type { Entity } from '@dxos/echo';
import { invariant } from '@dxos/invariant';
import { type PublicKey, type SpaceId } from '@dxos/keys';
import { log } from '@dxos/log';
import { RpcClosedError, subscribeStream } from '@dxos/protocols';
import { type DataService, type FeedService, type QueryService } from '@dxos/protocols/rpc';

import { type BranchStore, type DocumentRetention } from '../core-db/index.ts';
import { HypergraphImpl } from '../hypergraph.ts';
import { DatabaseImpl } from '../proxy-db/index.ts';
import { IndexQuerySourceProvider, type LoadObjectProps, type ObjectUpdate } from './index-query-source-provider.ts';

/** A root that has not linked an index hit by then may never; `linksAdded` re-hydrates it if it does. */
const ROOT_LINK_WAIT_TIMEOUT = 2_000;

export type EchoClientProps = {};

export type ConnectToServiceProps = {
  dataService: DataService.Client;
  queryService: QueryService.Client;
  feedService?: FeedService.Client;

  /** Runtime used to run effect-rpc service calls at Promise/callback boundaries. */
  runtime?: EffectContext.Context<never>;
};

export type ConstructDatabaseProps = {
  spaceId: SpaceId;

  /** @deprecated Use spaceId */
  spaceKey: PublicKey;

  /** Starts a new space epoch whose root is the given document; enables `beginMigration`. */
  createEpoch?: (rootUrl: string) => Promise<void>;

  /**
   * Run a reactive query for a set of dynamic schema.
   * @default true
   */
  reactiveSchemaQuery?: boolean;

  /**
   * Preload all schema during open.
   * @default true
   */
  preloadSchemaOnOpen?: boolean;

  /**
   * Space proxy reference for SDK compatibility.
   */
  // TODO(dmaretskyi): Remove.
  owningObject?: unknown;

  /** Device-local persistence for the current-branch selection (non-synced). In-memory if omitted. */
  branchStore?: BranchStore;

  /** Which loaded object documents the database keeps in memory. */
  documentRetention?: DocumentRetention;
};

/**
 * ECHO client.
 * Manages a set of databases and builds a unified hypergraph.
 * Connects to the ECHO host via an ECHO service.
 */
export class EchoClient extends Resource {
  private readonly _graph = new HypergraphImpl();

  // TODO(burdon): This already exists in Hypergraph.
  private readonly _databases = new Map<SpaceId, DatabaseImpl>();

  private _dataService: DataService.Client | undefined = undefined;
  private _queryService: QueryService.Client | undefined = undefined;
  private _feedService: FeedService.Client | undefined = undefined;
  private _runtime: EffectContext.Context<never> = EffectContext.empty();

  private _indexQuerySourceProvider: IndexQuerySourceProvider | undefined = undefined;

  /** Aggregated local object-update signal across all databases, consumed by index query sources. */
  private readonly _objectsUpdated = new Event<ObjectUpdate>();
  private readonly _dbUpdateSubscriptions = new Map<SpaceId, CleanupFn>();

  /** Each index status the host reports; see `DataService.IndexStatus`. */
  readonly indexStatusUpdated = new Event<DataService.IndexStatus>();

  /** Each index activity the host reports; see `DataService.IndexActivity`. */
  readonly indexActivityUpdated = new Event<DataService.IndexActivity>();

  #indexStatusCleanup: (() => void) | undefined;

  constructor(_: EchoClientProps = {}) {
    super();
  }

  get graph(): HypergraphImpl {
    return this._graph;
  }

  get openDatabases(): Iterable<DatabaseImpl> {
    return this._databases.values();
  }

  /**
   * Connects to the ECHO service.
   * Must be called before open.
   */
  connectToService({ dataService, queryService, feedService, runtime }: ConnectToServiceProps): this {
    invariant(this._lifecycleState === LifecycleState.CLOSED);
    this._dataService = dataService;
    this._queryService = queryService;
    this._feedService = feedService;
    this._runtime = runtime ?? EffectContext.empty();
    return this;
  }

  disconnectFromService(): void {
    invariant(this._lifecycleState === LifecycleState.CLOSED);
    this._dataService = undefined;
    this._queryService = undefined;
    this._feedService = undefined;
  }

  protected override async _open(ctx: Context): Promise<void> {
    invariant(this._dataService && this._queryService, 'Invalid state: not connected');

    this._indexQuerySourceProvider = this.#createIndexQuerySourceProvider(this._queryService);
    this._graph.registerQuerySourceProvider(this._indexQuerySourceProvider);
    this.#subscribeIndexStatus();
  }

  #subscribeIndexStatus(): void {
    invariant(this._dataService);
    this.#indexStatusCleanup?.();
    const onError = (err: Error) => {
      if (!(err instanceof RpcClosedError)) {
        log.warn('index status stream failed', { err });
      }
    };
    const status = subscribeStream(this._runtime, this._dataService['DataService.subscribeIndexStatus'](), {
      onData: (status) => this.indexStatusUpdated.emit(status),
      onError,
    });
    const activity = subscribeStream(this._runtime, this._dataService['DataService.subscribeIndexActivity'](), {
      onData: (activity) => this.indexActivityUpdated.emit(activity),
      onError,
    });
    this.#indexStatusCleanup = () => {
      status();
      activity();
    };
  }

  #createIndexQuerySourceProvider(service: QueryService.Client): IndexQuerySourceProvider {
    return new IndexQuerySourceProvider({
      service,
      runtime: this._runtime,
      objectLoader: {
        loadObject: this._loadObjectFromDocument.bind(this),
        updateEvent: this._objectsUpdated,
        subscriptionIds: this._subscriptionIds.bind(this),
      },
      graph: this._graph,
      indexProgress: this.indexStatusUpdated,
    });
  }

  protected override async _close(ctx: Context): Promise<void> {
    this.#indexStatusCleanup?.();
    this.#indexStatusCleanup = undefined;
    if (this._indexQuerySourceProvider) {
      this._graph.unregisterQuerySourceProvider(this._indexQuerySourceProvider);
    }
    for (const unsubscribe of this._dbUpdateSubscriptions.values()) {
      unsubscribe();
    }
    this._dbUpdateSubscriptions.clear();
    for (const db of this._databases.values()) {
      this._graph._unregisterDatabase(db.spaceId);
      await db.close(ctx);
    }
    this._databases.clear();
  }

  // TODO(dmaretskyi): Make async?
  constructDatabase({
    spaceId,
    owningObject,
    reactiveSchemaQuery,
    preloadSchemaOnOpen,
    spaceKey,
    branchStore,
    documentRetention,
    createEpoch,
  }: ConstructDatabaseProps): DatabaseImpl {
    invariant(this._lifecycleState === LifecycleState.OPEN);
    invariant(!this._databases.has(spaceId), 'Database already exists.');
    const db = new DatabaseImpl({
      dataService: this._dataService!,
      queryService: this._queryService!,
      feedService: this._feedService,
      runtime: this._runtime,
      graph: this._graph,
      spaceId,
      reactiveSchemaQuery,
      preloadSchemaOnOpen,
      spaceKey,
      branchStore,
      documentRetention,
      createEpoch,
    });
    this._graph._registerDatabase(spaceId, db, owningObject);
    this._databases.set(spaceId, db);

    // Forward this database's local object updates to the aggregated signal so reactive index
    // sources can re-hydrate index hits once their documents become available locally.
    const unsubscribeFromUpdates = db._entityManager._updateEvent.on((event) => {
      this._objectsUpdated.emit({ spaceId, objectIds: event.itemsUpdated.map((item) => item.id) });
    });
    // An index hit dropped because this client's space root did not route it yet is re-hydrated when
    // the root gains the link, rather than staying missing until the next host response.
    const unsubscribeFromLinks = db.linksAdded.on((objectIds) => {
      this._objectsUpdated.emit({ spaceId, objectIds });
    });
    this._dbUpdateSubscriptions.set(spaceId, () => {
      unsubscribeFromUpdates();
      unsubscribeFromLinks();
    });

    return db;
  }

  /**
   * Closes and unregisters a space's database, so the space can be constructed again should it
   * return (e.g. an identity deleted in place and then recovered brings back the same space ids).
   */
  removeDatabase(db: DatabaseImpl): Promise<void> {
    if (this._databases.get(db.spaceId) !== db) {
      return Promise.resolve();
    }
    this._databases.delete(db.spaceId);
    this._dbUpdateSubscriptions.get(db.spaceId)?.();
    this._dbUpdateSubscriptions.delete(db.spaceId);
    this._graph._unregisterDatabase(db.spaceId);
    return db.close().then(() => undefined);
  }

  /**
   * Update service references after reconnection.
   * Must be called before _notifyReconnect.
   */
  _updateServices({
    dataService,
    queryService,
    feedService,
  }: {
    dataService: DataService.Client;
    queryService: QueryService.Client;
    feedService?: FeedService.Client;
  }): void {
    log('updating service references');
    this._dataService = dataService;
    this._queryService = queryService;
    this._feedService = feedService;

    // Update IndexQuerySourceProvider with new service.
    if (this._indexQuerySourceProvider) {
      this._graph.unregisterQuerySourceProvider(this._indexQuerySourceProvider);
      this._indexQuerySourceProvider = this.#createIndexQuerySourceProvider(queryService);
      this._graph.registerQuerySourceProvider(this._indexQuerySourceProvider);
    }

    // Update all databases with new services.
    for (const db of this._databases.values()) {
      db._updateServices({ dataService, queryService, feedService });
    }
  }

  /**
   * Notify all databases that the service connection has been re-established.
   * Called after a dedicated worker leader change.
   */
  async _notifyReconnect(): Promise<void> {
    log('notifying databases of reconnection');
    if (this._lifecycleState === LifecycleState.OPEN) {
      this.#subscribeIndexStatus();
    }
    for (const db of this._databases.values()) {
      await db._onReconnect();
    }
  }

  private _subscriptionIds(spaceIds: readonly SpaceId[]): string[] {
    const databases =
      spaceIds.length > 0
        ? spaceIds.flatMap((spaceId) => this._databases.get(spaceId) ?? [])
        : [...this._databases.values()];
    return databases.map((db) => db._entityManager._repoProxy.subscriptionId);
  }

  private async _loadObjectFromDocument({
    spaceId,
    objectId,
    documentId,
  }: LoadObjectProps): Promise<Entity.Unknown | undefined> {
    const db = this._databases.get(spaceId);
    if (!db) {
      return undefined;
    }

    // Waiting for the database to open since the query can run before the database is ready.
    // TODO(dmaretskyi): Refactor this.
    try {
      await db.opened.wait();
    } catch (err) {
      if (err instanceof ContextDisposedError) {
        return undefined;
      }
      throw err;
    }

    const objectDocId = db.getObjectDocumentId(objectId) ?? (await this._waitForObjectLink(db, objectId));
    if (objectDocId !== documentId) {
      // Dropping the hit makes the result short, which reads to a caller as "no such object".
      log.warn('index hit dropped: the space root does not route the object to the indexed document', {
        objectId,
        expected: documentId,
        actual: objectDocId ?? null,
      });
      return undefined;
    }

    // Disk-only load: wait for dep states to settle, then return the core
    // only when strong deps are satisfied (unavailable deps → `undefined`).
    return db._loadObjectById(objectId, {
      allowDeleted: true,
      diskOnly: true,
    });
  }

  /**
   * The document the space root routes `objectId` to, once this client's replica of the root links it.
   * The index can learn of an object from the host's replica one sync batch before this one does.
   */
  private _waitForObjectLink(db: DatabaseImpl, objectId: string): Promise<string | undefined> {
    return new Promise((resolve) => {
      const settle = () => {
        clearTimeout(timer);
        unsubscribe();
        resolve(db.getObjectDocumentId(objectId));
      };
      // Links reach a loaded root by its change events and a passive one by its JSON; both report here.
      const unsubscribe = db.linksAdded.on((added) => {
        if (added.includes(objectId)) {
          settle();
        }
      });
      const timer = setTimeout(settle, ROOT_LINK_WAIT_TIMEOUT);
    });
  }
}
